import { natureDuBadge } from "@app/components/compta/portefeuille/natureDuBadge";
import { reviewBadge } from "@app/components/pod/cahier/format";
import { resolveCategorieJuridique } from "@app/lib/compta/references/categories_juridiques";
import type { ComptaMissionListItemType } from "@app/lib/resources/compta_mission_resource";
import { useComptaDossiers } from "@app/lib/swr/compta";
import { emptyArray } from "@app/lib/swr/swr";
import type { ComptaDossierListItemType } from "@app/types/api/compta/dossiers";
import type { ClientCabinet, FicheClient } from "@app/types/compta/clients";
import type { DossierClient } from "@app/types/compta/dossiers";
import type { EtatMetier } from "@app/types/compta/etats";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback, useMemo } from "react";

// Les dossiers du cabinet, pour les deux écrans de la portée cabinet :
// Portefeuille · Dossiers (une ligne par dossier, ses compteurs) et Clients
// (l'annuaire, la fiche). Une seule source, la liste cabinet, dont chaque ligne
// porte la DERNIÈRE révision comptée par le serveur.
//
// Ce que cet adaptateur refuse d'inventer, et le dit à l'écran : un contact
// que le cabinet n'a pas saisi (relances R1 : il vient du dossier), un portail
// de dépôt (il n'existe pas), un nombre de pièces, un exercice pour un dossier
// sans révision. Une colonne
// absente vaut mieux qu'un nom plausible dans un dossier de travail.

/** Un chiffre ou un texte que le serveur ne sert pas, même mot que le Cahier. */
export const VALEUR_ABSENTE = "n.d.";

/** « 842193507 » → « 842 193 507 », la forme que l'INSEE et l'écran utilisent. */
export function sirenAffiche(siren: string): string {
  return siren.replace(/(\d{3})(?=\d)/g, "$1 ");
}

/** « SARL LUMETTE » → « SL » : les deux premières initiales des mots. */
function initialesDe(raisonSociale: string): string {
  return raisonSociale
    .split(/\s+/)
    .filter((mot) => mot.length > 0)
    .slice(0, 2)
    .map((mot) => mot[0].toUpperCase())
    .join("");
}

// Relances R1 : le contact du dossier, quand le cabinet l'a saisi.
function contactDe(
  dossier: ComptaDossierListItemType
): { nom: string; email: string } | undefined {
  if (!dossier.contactName && !dossier.contactEmail) {
    return undefined;
  }
  return { nom: dossier.contactName ?? "", email: dossier.contactEmail ?? "" };
}

function raisonSocialeDe(dossier: ComptaDossierListItemType): string {
  return dossier.legalName ?? dossier.name;
}

/** L'année de clôture de l'exercice de la dernière révision, s'il y en a une. */
function exerciceDe(dossier: ComptaDossierListItemType): number | undefined {
  const fin = dossier.latestMission?.fiscalYear.endDate;
  return fin ? Number(fin.slice(0, 4)) : undefined;
}

/**
 * L'état de revue d'un dossier d'après sa dernière mission : le `reviewBadge`
 * du cahier, traduit dans la nature de la coquille. Le portefeuille, la fiche
 * client et l'Aperçu disent la MÊME chose, rien n'est redérivé.
 */
export function etatDeRevision(
  mission: ComptaMissionListItemType | null
): EtatMetier {
  if (mission === null) {
    return { libelle: "Aucune révision", nature: "neutre" };
  }
  const badge = reviewBadge(mission);
  return { libelle: badge.label, nature: natureDuBadge(badge.color) };
}

function statutDe(dossier: ComptaDossierListItemType): EtatMetier {
  return etatDeRevision(dossier.latestMission);
}

function ligneDePortefeuille(
  dossier: ComptaDossierListItemType
): DossierClient {
  const exercice = exerciceDe(dossier);
  return {
    // L'ESPACE, pas le sId du dossier : c'est lui que les routes du dossier
    // attendent (`spaces/:spaceId/apercu`, `/cahier`, `/revue`).
    id: dossier.spaceId,
    raisonSociale: raisonSocialeDe(dossier),
    ...(exercice !== undefined ? { exercice } : {}),
    // La lignée des compteurs [FR4] : la révision qui les a produits.
    ...(dossier.latestMission ? { missionId: dossier.latestMission.sId } : {}),
    // Les cycles CONCLUS par le réviseur, pas ceux calculés : afficher les
    // calculés ferait croire la revue avancée alors que personne n'a conclu.
    cycles: {
      faits: dossier.latestMission?.concludedCycleCount ?? 0,
      ...(dossier.latestMission
        ? { total: dossier.latestMission.cycleCount }
        : {}),
    },
    pointsOuverts: dossier.latestMission?.openFindingCount ?? 0,
    statut: statutDe(dossier),
  };
}

function ligneDAnnuaire(dossier: ComptaDossierListItemType): ClientCabinet {
  const raisonSociale = raisonSocialeDe(dossier);
  const contact = contactDe(dossier);
  return {
    ...(contact ? { contact } : {}),
    id: dossier.spaceId,
    initiales: initialesDe(raisonSociale),
    raisonSociale,
    siren: dossier.siren ? sirenAffiche(dossier.siren) : VALEUR_ABSENTE,
    // Un dossier = un espace, donc un dossier par client tant que la notion de
    // client à plusieurs dossiers n'existe pas côté serveur.
    nbDossiers: 1,
    // Le portail de dépôt n'existe pas encore : personne n'a été invité.
    portail: "non-invite",
  };
}

function sigleDe(code: string | null): string | null {
  return code ? resolveCategorieJuridique(code) : null;
}

// « SARL (5499) » ; le code seul quand la table ne le connaît pas.
function formeAffichee(code: string): string {
  const sigle = sigleDe(code);
  return sigle ? `${sigle} (${code})` : code;
}

function ficheDeDossier(dossier: ComptaDossierListItemType): FicheClient {
  const exercice = exerciceDe(dossier);
  const etat = statutDe(dossier);
  return {
    id: dossier.spaceId,
    raisonSociale: raisonSocialeDe(dossier),
    portail: "non-invite",
    anciennete: `client depuis ${dossier.createdAt.slice(0, 4)}`,
    codePostal: dossier.postalCode,
    ...(sigleDe(dossier.legalFormCode)
      ? { formeJuridique: sigleDe(dossier.legalFormCode) as string }
      : {}),
    // L'identité INSEE telle que servie.
    identite: [
      {
        etiquette: "SIREN",
        valeur: dossier.siren ? sirenAffiche(dossier.siren) : VALEUR_ABSENTE,
        mono: true,
      },
      {
        // Le sigle, et le code entre parenthèses : « 5499 » seul ne disait
        // rien à l'expert-comptable (recette de prod du 13/10).
        etiquette: "Forme juridique",
        valeur: dossier.legalFormCode
          ? formeAffichee(dossier.legalFormCode)
          : VALEUR_ABSENTE,
      },
      {
        etiquette: "Code APE",
        valeur: dossier.apeCode ?? VALEUR_ABSENTE,
        mono: true,
      },
      { etiquette: "Ville", valeur: dossier.city ?? VALEUR_ABSENTE },
      {
        etiquette: "Code postal",
        valeur: dossier.postalCode ?? VALEUR_ABSENTE,
        mono: true,
      },
    ],
    contact: contactDe(dossier) ?? null,
    contacts: (() => {
      const contact = contactDe(dossier);
      return contact
        ? [
            {
              id: "principal",
              initiales: initialesDe(contact.nom || contact.email),
              nom: contact.nom || contact.email,
              role: "contact principal",
              email: contact.email,
            },
          ]
        : [];
    })(),
    dossiers: [
      {
        id: dossier.spaceId,
        libelle: exercice !== undefined ? `Exercice ${exercice}` : dossier.name,
        etat,
      },
    ],
  };
}

export function useDossiersDuCabinet({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { dossiers, isDossiersLoading, isDossiersError, mutateDossiers } =
    useComptaDossiers({ owner, disabled });

  const lignes = useMemo<DossierClient[]>(
    () =>
      dossiers.length === 0
        ? emptyArray<DossierClient>()
        : dossiers.map(ligneDePortefeuille),
    [dossiers]
  );

  const clients = useMemo<ClientCabinet[]>(
    () =>
      dossiers.length === 0
        ? emptyArray<ClientCabinet>()
        : dossiers.map(ligneDAnnuaire),
    [dossiers]
  );

  /** Les dossiers par espace : une Map, pas un `find` par clic [GEN7]. */
  const parEspace = useMemo(
    () => new Map(dossiers.map((dossier) => [dossier.spaceId, dossier])),
    [dossiers]
  );

  const ficheDe = useCallback(
    (spaceId: string): FicheClient | null => {
      const dossier = parEspace.get(spaceId);
      return dossier ? ficheDeDossier(dossier) : null;
    },
    [parEspace]
  );

  // La pastille de l'onglet : une cardinalité, rien de plus. Mémorisée parce
  // que la coquille la lit dans un effet : un objet neuf à chaque rendu lui
  // ferait reposer sa configuration en boucle.
  const pastilles = useMemo(
    () => ({ dossiers: dossiers.length }),
    [dossiers.length]
  );

  return {
    dossiers: lignes,
    clients,
    ficheDe,
    pastilles,
    isDossiersLoading,
    isDossiersError,
    mutateDossiers,
  };
}
